import { useContext } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { UserContext } from '~/components/providers/UserProvider';

import {
  getPropPicksForEntries,
  getUserEntriesForContest,
} from '../domains/services/entries.service.api';
import { ContestQueryKeys } from '../constants/query';

type UsePlayerPropContestEntriesWithPicksProps = {
  contestId: string;
  pageSize?: number;
  enabled?: boolean;
};

const usePlayerPropContestEntriesWithPicks = ({
  contestId,
  pageSize = 20,
  enabled,
}: UsePlayerPropContestEntriesWithPicksProps) => {
  const { user } = useContext(UserContext);
  const userId = user?.id;

  // eslint-disable-next-line local-rules/require-query-stale-time
  const data = useInfiniteQuery({
    queryKey: ContestQueryKeys.ENTRIES_CURRENT_USER_WITH_PICKS({ contestId, userId }),
    queryFn: async ({ pageParam }) => {
      const entriesResponse = await getUserEntriesForContest(
        contestId,
        userId,
        pageSize,
        pageParam ?? 0
      );

      const picks = await getPropPicksForEntries({
        entryIds: entriesResponse.data.map(({ id }) => id),
      });

      const entriesData = entriesResponse.data;
      if (!entriesData || !entriesData.length) {
        return {
          entries: [],
          teamPicks: {},
          offset: entriesResponse.offset,
          total: entriesResponse.total,
        };
      }
      return {
        entries: entriesData,
        picks,
        total: entriesResponse.total,
        offset: entriesResponse.offset,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.entries.length === pageSize ? lastPage.offset + pageSize : undefined,
    enabled: enabled && !!contestId && !!userId,
  });

  return {
    ...data,
    itemsTotal: data.data?.pages[0]?.total,
  };
};

export default usePlayerPropContestEntriesWithPicks;
